// Lightweight inline-SVG charts for the admin dashboards. No runtime deps;
// keeps the bundle small and matches the existing inline-SVG icon style.
// Each chart is a fixed-height container (caller sets width via wrapper),
// uses Tailwind for label styling, and SVG for the data viz itself.
//
// IMPORTANT: every chart that fills its parent width measures the actual
// rendered width with a ResizeObserver and draws the SVG at 1:1 pixel scale
// (viewBox === pixel dimensions). The previous implementation used
// `viewBox="…" preserveAspectRatio="none"` which non-uniformly stretched
// the SVG to fill the container — that's fine for plain rects/paths but
// horizontally squashes/elongates <text> glyphs and made every label look
// distorted. Drawing at the measured pixel size avoids that entirely.

import { ReactNode, useEffect, useRef, useState } from 'react'

const GOLD = '#C8A96E'
const GOLD_DARK = '#B89856'
const INK = '#0F1117'
const INK_3 = '#9B9A95'
const SURFACE = '#F1E6CB'

// Status palette — kept in sync with the StatusBadge tones on Overview.
export const STATUS_COLOURS: Record<string, string> = {
  pending_payment: '#F59E0B', // amber
  paid:            '#10B981', // emerald
  in_production:   '#3B82F6', // blue
  shipped:         '#6366F1', // indigo
  delivered:       '#14B8A6', // teal
  cancelled:       '#EF4444', // red
  refunded:        '#F43F5E', // rose
  draft:           '#9CA3AF', // grey
  sent:            '#3B82F6',
  accepted:        '#10B981',
  rejected:        '#EF4444',
  expired:         '#A1A1AA',
  received:        '#10B981',
}

// Measures the rendered width of a div so we can draw SVGs at 1:1 pixel scale.
// Returns 0 on first render; charts guard with `width > 0` so the SVG only
// paints once we know what to draw against (avoids a flash of letterboxed
// content). One observer per chart instance, disconnected on unmount.
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // Seed with the initial layout width so the first paint has data.
    setWidth(el.clientWidth)
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0)
      if (w > 0) setWidth((prev) => (prev === w ? prev : w))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, width }
}

function fmtCents(c: number): string {
  if (c >= 100_000_00) return '$' + (c / 100_000_00).toFixed(1) + 'M'
  if (c >= 10_000_00)  return '$' + Math.round(c / 1000_00) + 'k'
  if (c >= 1_000_00)   return '$' + (c / 100_00).toFixed(1) + 'k'
  return '$' + (c / 100).toFixed(0)
}

// ── BarChart ───────────────────────────────────────────────────────────
// Vertical bars, one per data point. Auto-scales to the largest value.
// Labels: x-axis on the bottom, value on top of each bar.
export function BarChart({ data, height = 200, valueFormatter, colour = GOLD }: {
  data: Array<{ label: string; value: number; colour?: string }>
  height?: number
  valueFormatter?: (n: number) => string
  colour?: string
}) {
  const { ref, width } = useMeasuredWidth()
  if (data.length === 0) return <Empty height={height} />
  const max = Math.max(1, ...data.map((d) => d.value))
  const padTop = 28        // space above bars for value labels
  const padBottom = 28     // space below bars for axis labels
  const innerH = height - padTop - padBottom
  const fmt = valueFormatter ?? ((n: number) => String(n))

  // Each bar gets an equal-width column. Bar fills 60% of its column for a
  // little breathing room between neighbours.
  const slot = width > 0 ? width / data.length : 0
  const barW = Math.max(4, slot * 0.6)

  return (
    <div ref={ref} className="w-full" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height}>
          {/* Baseline */}
          <line x1={0} x2={width} y1={height - padBottom} y2={height - padBottom} stroke={SURFACE} strokeWidth={1} />
          {data.map((d, i) => {
            const h = Math.max(0, (d.value / max) * innerH)
            const cx = slot * i + slot / 2
            const x = cx - barW / 2
            const y = height - padBottom - h
            const c = d.colour ?? colour
            return (
              <g key={i}>
                <rect x={x} y={y} width={barW} height={h} rx={3} fill={c} opacity={0.92}>
                  <title>{d.label}: {fmt(d.value)}</title>
                </rect>
                <text x={cx} y={y - 6} textAnchor="middle" fontSize={11} fontFamily="ui-monospace" fill={INK}>{fmt(d.value)}</text>
                <text x={cx} y={height - 8} textAnchor="middle" fontSize={10} fill={INK_3}>{d.label}</text>
              </g>
            )
          })}
        </svg>
      )}
    </div>
  )
}

// ── DonutChart ─────────────────────────────────────────────────────────
// Single-ring donut. Centre slot for headline number, legend on the right.
export function DonutChart({ data, centerLabel, centerSub, height = 220 }: {
  data: Array<{ label: string; value: number; colour: string }>
  centerLabel?: string
  centerSub?: string
  height?: number
}) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <Empty height={height} />

  const cx = 90, cy = 90, rOuter = 80, rInner = 52
  let acc = 0
  const arcs = data.map((d) => {
    const a0 = (acc / total) * Math.PI * 2 - Math.PI / 2
    acc += d.value
    const a1 = (acc / total) * Math.PI * 2 - Math.PI / 2
    return { ...d, path: arcPath(cx, cy, rOuter, rInner, a0, a1), pct: (d.value / total) * 100 }
  })

  return (
    <div className="w-full flex items-center gap-4" style={{ minHeight: height }}>
      <svg viewBox="0 0 180 180" width={180} height={180} className="shrink-0">
        {arcs.map((a, i) => (
          <path key={i} d={a.path} fill={a.colour} opacity={0.92}>
            <title>{a.label}: {a.value} ({a.pct.toFixed(1)}%)</title>
          </path>
        ))}
        {/* Centre well */}
        {centerLabel && <text x={cx} y={cy - 4} textAnchor="middle" fontSize={22} fontWeight={700} fill={INK} fontFamily="ui-monospace">{centerLabel}</text>}
        {centerSub && <text x={cx} y={cy + 16} textAnchor="middle" fontSize={10} fill={INK_3} className="uppercase">{centerSub}</text>}
      </svg>
      <div className="text-xs space-y-1 flex-1 min-w-0">
        {data.map((d) => (
          <div key={d.label} className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.colour }} />
              <span className="text-ink-2 truncate">{d.label}</span>
            </div>
            <span className="text-ink-3 font-mono shrink-0">{d.value} <span className="text-ink-3/60">({Math.round((d.value / total) * 100)}%)</span></span>
          </div>
        ))}
      </div>
    </div>
  )
}

function arcPath(cx: number, cy: number, rOuter: number, rInner: number, a0: number, a1: number): string {
  const x0 = cx + rOuter * Math.cos(a0)
  const y0 = cy + rOuter * Math.sin(a0)
  const x1 = cx + rOuter * Math.cos(a1)
  const y1 = cy + rOuter * Math.sin(a1)
  const xi1 = cx + rInner * Math.cos(a1)
  const yi1 = cy + rInner * Math.sin(a1)
  const xi0 = cx + rInner * Math.cos(a0)
  const yi0 = cy + rInner * Math.sin(a0)
  const large = a1 - a0 > Math.PI ? 1 : 0
  return [
    `M ${x0} ${y0}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1}`,
    `L ${xi1} ${yi1}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${xi0} ${yi0}`,
    'Z',
  ].join(' ')
}

// ── LineChart ──────────────────────────────────────────────────────────
// Multi-series line over a shared x. Each series has its own colour.
// Auto-handles missing days — the caller's series should already have a
// row per day (back-end fills gaps if needed; this just draws what's
// given).
export function LineChart({ series, xLabels, height = 240, yFormatter }: {
  series: Array<{ label: string; data: number[]; colour: string }>
  xLabels: string[]
  height?: number
  yFormatter?: (n: number) => string
}) {
  const { ref, width } = useMeasuredWidth()
  if (xLabels.length === 0) return <Empty height={height} />

  const padL = 40, padR = 12, padT = 16, padB = 28
  const innerW = Math.max(50, width - padL - padR)
  const innerH = height - padT - padB
  const flat = series.flatMap((s) => s.data)
  const max = Math.max(1, ...flat)
  const fmt = yFormatter ?? ((n) => String(n))

  const xAt = (i: number) => padL + (xLabels.length === 1 ? innerW / 2 : (i / (xLabels.length - 1)) * innerW)
  const yAt = (v: number) => padT + innerH - (v / max) * innerH

  // 4 y-axis grid lines + labels.
  const ticks = [0, max / 4, max / 2, (max * 3) / 4, max]

  // x-axis tick stride — only label every Nth so it doesn't overlap.
  const stride = Math.ceil(xLabels.length / 8)

  return (
    <div ref={ref} className="w-full" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height}>
          {/* Grid + y labels */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={padL} x2={width - padR} y1={yAt(t)} y2={yAt(t)} stroke={SURFACE} strokeWidth={1} />
              <text x={padL - 6} y={yAt(t) + 3} textAnchor="end" fontSize={9} fill={INK_3} fontFamily="ui-monospace">{fmt(t)}</text>
            </g>
          ))}
          {/* x labels */}
          {xLabels.map((label, i) => (i % stride === 0
            ? <text key={i} x={xAt(i)} y={height - 8} textAnchor="middle" fontSize={9} fill={INK_3}>{label}</text>
            : null))}
          {/* Lines */}
          {series.map((s, si) => {
            const points = s.data.map((v, i) => `${xAt(i)},${yAt(v)}`).join(' ')
            return (
              <g key={si}>
                <polyline points={points} fill="none" stroke={s.colour} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {s.data.map((v, i) => (
                  <circle key={i} cx={xAt(i)} cy={yAt(v)} r={2.5} fill={s.colour}>
                    <title>{xLabels[i]}: {fmt(v)}</title>
                  </circle>
                ))}
              </g>
            )
          })}
        </svg>
      )}
      {/* Legend */}
      {series.length > 1 && (
        <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-ink-2">
          {series.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.colour }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── StackedBar ─────────────────────────────────────────────────────────
// A single horizontal bar with proportional segments. Tiny — good as a
// "by status" indicator under a counter tile.
export function StackedBar({ data, height = 8 }: {
  data: Array<{ label: string; value: number; colour: string }>
  height?: number
}) {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return <div className="h-2 bg-surface rounded" style={{ height }} />
  return (
    <div className="w-full flex rounded overflow-hidden" style={{ height }}>
      {data.map((d, i) => (
        <span key={i} title={`${d.label}: ${d.value}`} style={{ flex: d.value, background: d.colour }} />
      ))}
    </div>
  )
}

// ── KPI tile ───────────────────────────────────────────────────────────
// Headline number + label, optional delta vs comparison period.
export function KpiTile({ label, value, sub, delta, hint, accent }: {
  label: string
  value: string
  sub?: string
  delta?: { value: number; positiveIsGood?: boolean }
  hint?: string
  accent?: 'gold' | 'emerald' | 'amber' | 'red' | 'blue'
}) {
  const accentClass: Record<string, string> = {
    gold:    'border-l-gold',
    emerald: 'border-l-emerald-500',
    amber:   'border-l-amber-500',
    red:     'border-l-red-500',
    blue:    'border-l-blue-500',
  }
  const positive = (delta?.value ?? 0) > 0
  const negative = (delta?.value ?? 0) < 0
  const positiveIsGood = delta?.positiveIsGood ?? true
  const good = positive && positiveIsGood || negative && !positiveIsGood
  return (
    <div className={'card border-l-4 ' + (accent ? accentClass[accent] : 'border-l-border')}>
      <div className="eyebrow !mb-1">{label}</div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <div className="font-heading text-3xl font-bold text-ink">{value}</div>
        {sub && <div className="text-xs text-ink-3">{sub}</div>}
      </div>
      {delta && delta.value !== 0 && (
        <div className={'mt-1 text-[11px] font-semibold ' + (good ? 'text-emerald-700' : negative ? 'text-red-700' : 'text-ink-3')}>
          {positive ? '▲' : '▼'} {Math.abs(delta.value).toFixed(0)}%
          <span className="text-ink-3 font-normal"> vs prev period</span>
        </div>
      )}
      {hint && <div className="text-[11px] text-ink-3 mt-1">{hint}</div>}
    </div>
  )
}

function Empty({ height, children }: { height: number; children?: ReactNode }) {
  return (
    <div className="w-full flex items-center justify-center text-[11px] text-ink-3" style={{ height }}>
      {children ?? 'No data yet.'}
    </div>
  )
}

export { fmtCents, GOLD, GOLD_DARK }
